import React, { useEffect, useState } from 'react';
import { usePage } from '@inertiajs/react';
import NavLink from '@/elements/NavLink';
import { useCountries } from '@/hooks/useCountries';

// One entry per animation stage; each stage holds for its duration, then the
// loop advances and wraps back to the empty chat (stage 0).
const STAGE_DURATIONS = [600, 1500, 1300, 1600, 1500, 1400, 1300, 1600, 4200];
const FULL_CONVERSATION = STAGE_DURATIONS.length - 1;

export function ClocheIcon({ size, color }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
            <path
                d="M12 5.2c-4.6 0-8.3 3.5-8.6 8h17.2c-.3-4.5-4-8-8.6-8Z"
                fill={color}
            />
            <circle cx="12" cy="3.4" r="1.5" fill={color} />
            <path
                d="M2.6 16h18.8"
                stroke={color}
                strokeWidth="1.8"
                strokeLinecap="round"
            />
        </svg>
    );
}

function UserMessage({ children }) {
    return (
        <div className="flex justify-end animate-butler-msg-in motion-reduce:animate-none">
            <div className="max-w-[82%] rounded-[13px_13px_4px] bg-brand-black px-3 py-[9px] text-[12.5px] leading-[1.45] text-white lg:max-w-[78%] lg:rounded-[14px_14px_4px] lg:px-3.5 lg:py-2.5 lg:text-[13.5px]">
                {children}
            </div>
        </div>
    );
}

function BotAvatar() {
    return (
        <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-brand-violet-200 lg:h-[26px] lg:w-[26px]">
            <ClocheIcon size={13} color="#5A35C0" />
        </span>
    );
}

function BotMessage({ children }) {
    return (
        <div className="flex items-end gap-[7px] animate-butler-msg-in motion-reduce:animate-none lg:gap-2">
            <BotAvatar />
            <div className="max-w-[84%] rounded-[13px_13px_13px_4px] bg-[#F5F5F7] px-3 py-[9px] text-[12.5px] leading-[1.45] text-brand-black lg:max-w-[80%] lg:rounded-[14px_14px_14px_4px] lg:px-3.5 lg:py-2.5 lg:text-[13.5px]">
                {children}
            </div>
        </div>
    );
}

function TypingIndicator() {
    return (
        <div className="flex items-end gap-[7px] animate-butler-msg-in motion-reduce:animate-none lg:gap-2">
            <BotAvatar />
            <div className="flex gap-[5px] rounded-[13px_13px_13px_4px] bg-[#F5F5F7] px-4 py-3 lg:rounded-[14px_14px_14px_4px]">
                <span className="h-[7px] w-[7px] rounded-full bg-[#828894] animate-butler-dot motion-reduce:animate-none" />
                <span className="h-[7px] w-[7px] rounded-full bg-[#828894] animate-butler-dot [animation-delay:180ms] motion-reduce:animate-none" />
                <span className="h-[7px] w-[7px] rounded-full bg-[#828894] animate-butler-dot [animation-delay:360ms] motion-reduce:animate-none" />
            </div>
        </div>
    );
}

/**
 * Homepage showcase for Butler, rendered directly under the hero. The left
 * card is a scripted, looping demo of the chat - not the real widget - and the
 * CTA navigates to the actual chat at route `butler`. Rendering this at all is
 * the caller's decision, gated on the `butlerCta` shared prop, which also
 * drives the Butler item in the mobile sticky nav.
 */
export default function ButlerShowcase() {
    const { country } = usePage().props;
    const countries = useCountries();
    const sign = (countries[country] || countries['GB'])?.currency_sign || '£';

    const [stage, setStage] = useState(0);

    useEffect(() => {
        // Jump straight to the settled conversation instead of a slower loop;
        // the per-element motion is cancelled by `motion-reduce:animate-none`.
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            setStage(FULL_CONVERSATION);
            return undefined;
        }

        let current = 0;
        let timer = setTimeout(function step() {
            current = (current + 1) % STAGE_DURATIONS.length;
            setStage(current);
            timer = setTimeout(step, STAGE_DURATIONS[current]);
        }, STAGE_DURATIONS[0]);

        return () => clearTimeout(timer);
    }, []);

    return (
        <section className="relative my-6 overflow-hidden rounded-[20px] border-[1px] border-solid border-brand-violet-200 bg-[linear-gradient(160deg,#F2EFFB,#FFF_55%,#F8F5F2)] p-[24px_18px] lg:my-8 lg:rounded-3xl lg:bg-[linear-gradient(300deg,#F2EFFB,#FFF_55%,#F8F5F2)] lg:p-[40px_44px]">
            <svg
                viewBox="0 0 1052 480"
                fill="none"
                preserveAspectRatio="xMidYMid slice"
                className="pointer-events-none absolute inset-0 hidden h-full w-full lg:block"
                aria-hidden="true"
            >
                <defs>
                    <linearGradient
                        id="butlerShowcaseRingTR"
                        x1="880"
                        y1="-190"
                        x2="880"
                        y2="330"
                        gradientUnits="userSpaceOnUse"
                    >
                        <stop offset="0" stopColor="#C1B2EA" stopOpacity="0" />
                        <stop
                            offset=".55"
                            stopColor="#C1B2EA"
                            stopOpacity=".65"
                        />
                        <stop offset="1" stopColor="#C1B2EA" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient
                        id="butlerShowcaseRingBL"
                        x1="120"
                        y1="270"
                        x2="290"
                        y2="480"
                        gradientUnits="userSpaceOnUse"
                    >
                        <stop offset="0" stopColor="#E7DDD4" stopOpacity=".9" />
                        <stop offset="1" stopColor="#E7DDD4" stopOpacity="0" />
                    </linearGradient>
                </defs>
                <circle
                    cx="920"
                    cy="40"
                    r="200"
                    stroke="url(#butlerShowcaseRingTR)"
                    strokeWidth="1.2"
                />
                <circle
                    cx="920"
                    cy="40"
                    r="278"
                    stroke="url(#butlerShowcaseRingTR)"
                    strokeWidth="1"
                    opacity=".6"
                />
                <circle
                    cx="120"
                    cy="450"
                    r="175"
                    stroke="url(#butlerShowcaseRingBL)"
                    strokeWidth="1.2"
                />
                <path
                    d="M986 210c.45 3.6 2 5.3 5.7 5.7-3.7.45-5.25 2.1-5.7 5.7-.45-3.6-2-5.25-5.7-5.7 3.7-.45 5.25-2.1 5.7-5.7Z"
                    fill="#C1B2EA"
                    className="animate-butler-twinkle motion-reduce:animate-none"
                    style={{
                        transformOrigin: '986px 216px',
                        animationDelay: '1.1s',
                    }}
                />
                <path
                    d="M52 96c.4 3.2 1.75 4.7 5 5.1-3.25.4-4.6 1.9-5 5.1-.4-3.2-1.75-4.7-5-5.1 3.25-.4 4.6-1.9 5-5.1Z"
                    fill="#C1B2EA"
                    opacity=".8"
                    className="animate-butler-twinkle motion-reduce:animate-none"
                    style={{
                        transformOrigin: '52px 101px',
                        animationDelay: '0.6s',
                    }}
                />
            </svg>
            <svg
                viewBox="0 0 358 560"
                fill="none"
                preserveAspectRatio="xMidYMid slice"
                className="pointer-events-none absolute inset-0 h-full w-full lg:hidden"
                aria-hidden="true"
            >
                <defs>
                    <linearGradient
                        id="butlerShowcaseRingM"
                        x1="330"
                        y1="-120"
                        x2="330"
                        y2="240"
                        gradientUnits="userSpaceOnUse"
                    >
                        <stop offset="0" stopColor="#C1B2EA" stopOpacity="0" />
                        <stop
                            offset=".55"
                            stopColor="#C1B2EA"
                            stopOpacity=".6"
                        />
                        <stop offset="1" stopColor="#C1B2EA" stopOpacity="0" />
                    </linearGradient>
                </defs>
                <circle
                    cx="340"
                    cy="30"
                    r="130"
                    stroke="url(#butlerShowcaseRingM)"
                    strokeWidth="1.2"
                />
                <path
                    d="M36 34c.4 3.2 1.75 4.7 5 5.1-3.25.4-4.6 1.9-5 5.1-.4-3.2-1.75-4.7-5-5.1 3.25-.4 4.6-1.9 5-5.1Z"
                    fill="#C1B2EA"
                    className="animate-butler-twinkle motion-reduce:animate-none"
                    style={{
                        transformOrigin: '36px 39px',
                        animationDelay: '0.6s',
                    }}
                />
            </svg>

            <div className="relative flex flex-col lg:flex-row-reverse lg:items-center lg:gap-12">
                <div className="lg:flex-1">
                    <span className="inline-flex text-[11.5px] font-bold tracking-[0.22em] text-brand-violet-650 lg:text-[12.5px]">
                        JUST LAUNCHED
                    </span>
                    <h2 className="mb-0 mt-2.5 text-2xl font-bold leading-[1.22] text-brand-black lg:mt-3.5 lg:text-4xl lg:leading-[1.2]">
                        Meet Butler, your very own concierge
                    </h2>
                    <p className="mb-0 mt-2 text-[14.5px] text-[#40495C] lg:mt-2.5 lg:max-w-[480px] lg:text-[16.5px]">
                        Say it how you'd say it - tapas for 12, Mum's 70th, two
                        vegetarians - and see real prices instantly.
                    </p>
                    <div className="mt-4 lg:mt-[22px]">
                        <NavLink
                            routeName="butler"
                            className="inline-flex w-full items-center justify-center rounded-lg bg-brand-violet-700 px-5 py-3 text-base font-bold text-white no-underline transition-colors duration-300 hover:bg-brand-violet-darker hover:text-white hover:no-underline lg:w-auto"
                            aria-label="Ask Butler anything"
                            title="Ask Butler anything"
                        >
                            Ask Butler anything
                        </NavLink>
                    </div>
                </div>

                {/* Scripted demo, not a live conversation - hidden from the
                    accessibility tree so the copy above is the single answer
                    to "what is this section". */}
                <div
                    className="relative mt-4 w-full overflow-hidden rounded-2xl border-[1px] border-solid border-[#E6E9F0] bg-white shadow-[0_18px_40px_rgba(29,29,53,0.14)] lg:mt-0 lg:w-[400px] lg:flex-none lg:rounded-[20px] lg:shadow-[0_24px_54px_rgba(29,29,53,0.16)]"
                    aria-hidden="true"
                >
                    <div className="flex items-center gap-[9px] border-b border-[#F0F0F3] px-3.5 py-[11px] lg:gap-2.5 lg:px-[18px] lg:py-[13px]">
                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-violet-darker lg:h-8 lg:w-8">
                            <ClocheIcon size={15} color="#FFF" />
                        </span>
                        <span className="text-sm font-bold text-brand-black lg:text-[15px]">
                            Butler
                        </span>
                    </div>
                    <div className="flex h-[420px] flex-col gap-2 overflow-hidden p-[12px_12px_10px] lg:h-[392px] lg:gap-[9px] lg:p-[14px_16px_12px]">
                        {stage >= 1 && (
                            <UserMessage>
                                Looking for a relaxed birthday dinner for 8 next
                                Saturday. Italian maybe?
                            </UserMessage>
                        )}
                        {stage === 2 && <TypingIndicator />}
                        {stage >= 3 && (
                            <BotMessage>
                                Lovely! <strong>12 Italian chefs</strong> are
                                free that night. Three courses usually runs{' '}
                                <strong>
                                    {sign}60–{sign}70pp
                                </strong>
                                .
                            </BotMessage>
                        )}
                        {stage >= 4 && (
                            <div className="ml-[31px] flex items-center justify-between gap-2 rounded-[11px] border-[1px] border-solid border-brand-violet-200 bg-white p-[10px_12px] animate-butler-msg-in motion-reduce:animate-none lg:ml-[34px] lg:gap-2.5 lg:rounded-xl lg:p-[12px_14px]">
                                <div>
                                    <div className="text-[11px] text-[#828894] lg:text-[11.5px]">
                                        Dinner for 8 · Italian
                                    </div>
                                    <div className="text-[15px] font-bold text-brand-black lg:text-[17px]">
                                        {sign}480 – {sign}560 total
                                    </div>
                                </div>
                                <span className="inline-flex items-center gap-[5px] rounded-full bg-[#E9F7F0] px-[9px] py-[3px] text-[10.5px] font-bold text-[#101828] lg:gap-1.5 lg:px-2.5 lg:py-1 lg:text-[11.5px]">
                                    <span className="h-1.5 w-1.5 rounded-full bg-[#04A777] animate-butler-pulse motion-reduce:animate-none" />
                                    LIVE
                                </span>
                            </div>
                        )}
                        {stage >= 5 && (
                            <UserMessage>
                                Can we keep it under {sign}500?
                            </UserMessage>
                        )}
                        {stage === 6 && <TypingIndicator />}
                        {stage >= 7 && (
                            <BotMessage>
                                Done — <strong>5 chefs</strong> fit your budget.
                                Want me to hold Sat 22 while you choose?
                            </BotMessage>
                        )}
                        {stage >= 8 && (
                            <div className="ml-[31px] flex gap-2 animate-butler-msg-in motion-reduce:animate-none lg:ml-[34px]">
                                <span className="rounded-full bg-brand-violet-650 px-3.5 py-[7px] text-[12.5px] font-bold text-white">
                                    Hold my date
                                </span>
                                <span className="rounded-full border-[1px] border-solid border-brand-grey-300 px-3.5 py-[7px] text-[12.5px] font-bold text-brand-black">
                                    See the 5 chefs
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
